import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import { resolveGapClasses, type Space } from "./foundations/space";

const SPREAD_ALIGN_CLASSES = {
  stretch: "items-stretch",
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
} as const;

type SpreadElement = "div" | "li" | "header" | "footer";

export type SpreadProps<TElement extends SpreadElement = "div"> = {
  children: ReactNode;
  space?: Space;
  align?: keyof typeof SPREAD_ALIGN_CLASSES;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** A single non-wrapping row whose children occupy opposite ends. */
export function Spread<TElement extends SpreadElement = "div">({
  align = "center",
  as,
  children,
  ref,
  space,
  ...nativeProps
}: SpreadProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin("flex justify-between", SPREAD_ALIGN_CLASSES[align], resolveGapClasses(space))}
    >
      {children}
    </Element>
  );
}
